import React from "react";
import { FiLayers, FiGlobe } from "react-icons/fi";
import { FaVuejs, FaCube, FaBolt, FaRocket } from "react-icons/fa";
import { LuSettings, LuUsers, LuSmartphone, LuShieldCheck, LuShield, LuPercent, LuCircleCheck, LuSearch, LuClipboardList, LuPalette, LuCode, LuRocket } from "react-icons/lu";

// 1. Hero Block
export const VueAppHeroData = {
  backgroundImage: "/images/serviceBanners/VueBanner.webp",
  whiteImage: "/images/serviceBanners/VueueW.webp",
  subTitle: "Vue.js Development Services",
  title: (
    <>
      Lightweight & <br /> Reactive <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Vue.js Applications</span>
    </>
  ),
  description:
    "We build fast, lightweight, and reactive Vue.js applications using modern frameworks to deliver smooth performance, flexible architecture, and highly interactive user experiences for scalable and efficient web solutions.",
  primaryButton: {
    title: "Get a Free Quote",
    onClick: () => console.log("Get Quote clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Fast Load Times",
      desc: "Optimized Vue apps for quick rendering and better performance.",
      iconColor: "#42b883",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Reactive UI",
      desc: "Real-time UI updates for dynamic and smooth interactions.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Flexible Tech Stack",
      desc: "Adaptable architecture for projects of any size.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Developer Friendly",
      desc: "Simple APIs and powerful ecosystem for faster development.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg",
      top: "12%",
      right: "10%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(66,184,131,0.35)",
      iconGlow: "rgba(66,184,131,0.9)",
    },
  ],
};

// 2. Stats Block
export const vueStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const vueSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Graceful Vue.js Development Solutions for Modern Applications",
  buildCards: [
    {
      title: "Custom Vue Apps",
      desc: "Build modern and reactive Vue.js applications for scalable use cases.",
      icon: FaVuejs,
      colorClass: "text-[#42b883]",
      bgClass: "bg-[#42b883]/10",
      borderClass: "border-[#42b883]/20 hover:border-[#42b883]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(66,184,131,0.25)]",
    },
    {
      title: "Nuxt.js SSR",
      desc: "Create SEO-friendly server-side rendered applications with the Nuxt framework.",
      icon: FiLayers,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "State Management",
      desc: "Manage data efficiently using Pinia and Vuex for scalable storage architectures.",
      icon: FaCube,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "High Performance",
      desc: "Optimize speed and performance using Vite and modern build workflows.",
      icon: FaBolt,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Component Architecture",
      desc: "Build reusable and modular Vue components optimized for enterprise scale.",
      icon: FiLayers,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "API Integration",
      desc: "Connect frontend layers with complex backend services seamlessly and securely.",
      icon: FiGlobe,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "VUE.JS DEVELOPMENT PROCESS",
  processTitle: "Vue.js Development Process",
  processSteps: [
    {
      step: "01",
      title: "Requirement Analysis",
      description: "Define project scope and plan how Vue.js features solve business goals.",
      icon: LuSearch,
    },
    {
      step: "02",
      title: "Architecture Planning",
      description: "Design scalable Vue structure for maximum performance and maintainability.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Prototyping & Development",
      description: "Build interactive UI workflows using Vue’s reactive component core system.",
      icon: LuPalette,
    },
    {
      step: "04",
      title: "API Integration",
      description: "Connect frontend with backend ecosystems for reactive and dynamic data flow.",
      icon: LuCode,
    },
    {
      step: "05",
      title: "Optimization & Testing",
      description: "Improve performance benchmarks and ensure an entirely bug-free user experience.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Deployment & Launch",
      description: "Deploy optimized Vue applications smoothly using stable production configurations.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY PARTNER WITH US FOR VUE.JS DEVELOPMENT?",
  benefitsTitle: "Flexible & Reactive Vue Architecture",
  benefits: [
    "Lightweight Vue architecture structures",
    "Flexible and scalable application frameworks",
    "Fast growth ecosystem and extension tool support",
    "Reactive component patterns out of the box",
    "Data-driven UI updates with minimized rendering lag",
    "Modern tooling ecosystems powered by Vite integration",
    "SEO-ready configurations with custom Nuxt SSR modules",
  ],
  logoImage: "/images/serviceBanners/VueBanner.webp",
  whiteLogoImage: "/images/serviceBanners/VueueW.webp",
};

// 4. Portfolio Data
export const vuePortfolioData = {
  subtitle: "OUR WORK",
  title: "Modern Vue Portals We've Built",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "Dynamic Dashboard",
      badge: "SaaS Application",
      badgeColor: "bg-[#42b883]/10 text-[#42b883] border-[#42b883]/20",
      desc: "A reactive data visualization platform with real-time updates and seamless state management.",
      downloads: "Active",
      rating: "4.9",
      image: "/images/cms-dev.jpg",
    },
    {
      title: "Nuxt-Store",
      badge: "E-Commerce",
      badgeColor: "bg-[#3b82f6]/10 text-[#3b82f6] border-[#3b82f6]/20",
      desc: "Fast, SEO-optimized shopping experience built with Nuxt.js and server-side rendering.",
      downloads: "SEO Optimized",
      rating: "4.8",
      image: "/images/webapp-dev.jpg",
    },
  ],
};

// 5. Technologies Data
export const vueTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "Vue.js", icon: "Vue" },
    { name: "Nuxt.js", icon: "Nuxt" },
    { name: "Pinia", icon: "Pinia" },
    { name: "Vue Router", icon: "VueRouter" },
    { name: "Vite", icon: "Vite" },
    { name: "Vuetify", icon: "Vuetify" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 6. About Overview
export const vueAboutData = [
  {
    title: "Vue JS Development Services for Fast and Modern Applications",
    content: (
      <>
        Modern businesses need web applications that are lightweight, responsive, and easy to scale as customer expectations evolve. Reliable{" "}
        <b className="text-text-primary font-semibold">vue js development services</b> help organizations create smooth digital experiences that improve usability and application
        performance. Many businesses struggle with slow interfaces and complex frontend systems that impact user engagement. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, Vue.js applications are developed around flexibility, business goals, and modern user expectations to
        help brands create faster and more engaging digital experiences.
      </>
    ),
  },
  {
    title: "Vue JS Development Company for Better User Experiences",
    content: (
      <>
        A successful web application depends on how smoothly users can interact with features and content. A trusted{" "}
        <b className="text-text-primary font-semibold">vue js development company</b> helps businesses create responsive interfaces that improve navigation, engagement, and overall
        usability. Companies investing in <b className="text-text-primary font-semibold">vue js web development</b> often benefit from better flexibility because applications can
        adapt to different business requirements without unnecessary complexity. Tycho Technologies focuses on practical frontend systems designed to improve digital performance
        while supporting long-term scalability.
      </>
    ),
  },
  {
    title: "Vue JS App Development Services for Scalable Digital Products",
    content: (
      <>
        Businesses aiming to improve frontend performance often prioritize technologies that support faster updates and smooth interaction. Reliable{" "}
        <b className="text-text-primary font-semibold">vue js app development services</b> help organizations build dynamic applications that improve customer experiences and
        simplify user journeys. Companies looking to strengthen frontend flexibility frequently combine modern frameworks with{" "}
        <a href="https://tychotechnologies.com/solidjs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          solidjs development services
        </a>{" "}
        to support scalable product ecosystems and improve digital performance.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Vue JS Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">vue js development company in india</b> continues to grow as businesses seek flexible and
        performance-focused frontend technologies. Companies looking to <b className="text-text-primary font-semibold">hire vue js developers</b> often prioritize maintainability,
        faster implementation, and better user interaction quality. Businesses partnering with Tycho Technologies gain access to customized Vue.js strategies designed around
        measurable business goals and user expectations. Many organizations also improve frontend scalability through{" "}
        <a href="https://tychotechnologies.com/angular-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          angular development services
        </a>{" "}
        to create stronger and future-ready digital products.
      </>
    ),
  },
];

// 7. FAQs
export const vueFaqs = [
  {
    q: "Why should I choose Vue Js development services in India?",
    a: "Vue Js development in India offers a great balance of quality and affordability. Indian developers are highly skilled in Vue.js, follow global coding standards, and provide flexible engagement models.",
  },
  {
    q: "What is Vue.js development and when should I use it?",
    a: "It's ideal when you need a fast, lightweight, and scalable front-end solution. Vue.js works well for single-page applications, dashboards, and dynamic user interfaces.",
  },
  {
    q: "Will Vue.js work with existing projects?",
    a: "Yes, Vue.js is designed to be incrementally adoptable, meaning it can be easily integrated into existing projects without a full rewrite.",
  },
];

// Fallback for legacy components
export const vueTechStack = ["Vue.js", "Nuxt.js", "Pinia", "Vue Router", "Vite", "Vuetify", "Element Plus"];
